// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = {
    title?: string;
    icon?: any;
    viewAllLink?: string;
    viewAllText?: string;
    extendedStyles?: WidgetTitleStyles;
};

export interface WidgetTitleStyles {
    wrapper?: InjectableCss;
    iconWrapper?: InjectableCss;
    iconElement?: IconPresentationProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const widgetTitleStyles: WidgetTitleStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: baseline;
            border-top: 2px solid ${getColor("common.ui3")};
            padding-top: 20px;
            flex-wrap: wrap;
            margin-bottom: 32px;
            &&.with-icon {
                padding-left: 70px;
                position: relative;
                ${xScreen("lte", "xs")} {
                    align-items: center;
                }
            }
            @media print {
                display: none;
            }
        `,
    },
    iconWrapper: {
        css: css`
            line-height: 1;
            width: 55px;
            height: 61px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            left: 0;
            border-radius: 0 0 15px 15px;
            top: 0;
            && > span,
            && svg {
                height: 100%;
                width: 100%;
            }
        `,
    },
    iconElement: {
        color: "common.white",
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    link: {
        typographyProps: {
            css: css`
                line-height: ${fontConfig.lineHeight.singleLineSmall} !important;
            `,
        },
        css: css`
            margin-left: 15px;
            svg {
                margin-bottom: 4px;
            }
        `,
    },
};

const WidgetTitle = ({ title, icon, viewAllLink, viewAllText, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeToNew(widgetTitleStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper} className={icon ? "with-icon" : ""} data-test-selector="WidgetTitle">
            <StyledWrapper {...styles.iconWrapper}>{icon && <Icon src={icon} {...styles.iconElement} />}</StyledWrapper>
            <Typography as="h2" {...styles.title}>
                {title}
            </Typography>
            {viewAllLink && (
                <Link href={viewAllLink} color="primary.main" {...styles.link}>
                    {viewAllText || translate("View All")}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default WidgetTitle;
